import { InfoTooltip, ProCard } from '@components'
import TrendPill from '@dashboard/components/TrendPill'
import type { TrendDelta } from '@dashboard/types/analytics.types'
import cn from '@integrations/classnames'
import type { ReactNode } from '@integrations/react'
import { tr } from 'mmfintech-commons'
import kpiCardsStyles from '../KpiCards/KpiCards.module.scss'

interface KpiCardProps {
  children: ReactNode
  /** Spans the full row when the strip wraps — reserved for the strip's lead metric. */
  hero?: boolean
  className?: string
}

// A KPI sits on the shared ProCard surface; this family only arranges the metric inside it.
function KpiCard({ children, hero, className }: KpiCardProps) {
  return (
    <ProCard
      className={cn('kpi-card', className, {
        'kpi-card--hero': hero
      })}
    >
      {children}
    </ProCard>
  )
}

interface HeaderProps {
  label: string
  /** Explanation of the metric, shown in an info tooltip next to the label. */
  info?: ReactNode
}

/**
 * The metric name, above the figure. Also borrowed by the Refer & Earn cards for the label
 * typography inside their own `ProCard`.
 */
function Header({ label, info }: HeaderProps) {
  const text = <span className={cn(kpiCardsStyles['kpi-card__label'], 'kpi-card__label')}>{label}</span>

  return info ? (
    <span className={cn(kpiCardsStyles['kpi-card__heading'], 'kpi-card__heading')}>
      {text}
      <InfoTooltip
        variant='outline'
        label={label}
        triggerLabel={tr('FRONTEND.DASHBOARD.KPI.INFO_LABEL', 'More about {{METRIC}}', { METRIC: label })}
        content={info}
      />
    </span>
  ) : (
    text
  )
}

interface ValueProps {
  children: ReactNode
  /** Trailing unit (e.g. a currency code), set smaller than the figure. */
  unit?: string
}

function Value({ children, unit }: ValueProps) {
  return (
    <div className={cn(kpiCardsStyles['kpi-card__value'], 'kpi-card__value')}>
      {children}
      {unit && <span className={cn(kpiCardsStyles['kpi-card__unit'], 'kpi-card__unit')}>{unit}</span>}
    </div>
  )
}

interface SubProps {
  children: ReactNode
}

function Sub({ children }: SubProps) {
  return <p className={cn(kpiCardsStyles['kpi-card__sub'], 'kpi-card__sub')}>{children}</p>
}

interface TrendProps {
  delta: TrendDelta | null
}

// Always occupies its row, delta or not: without the placeholder, a metric with no comparable
// previous period loses its third line and its value stops sharing a baseline with its neighbours.
function Trend({ delta }: TrendProps) {
  if (!delta) {
    return (
      <div className={cn(kpiCardsStyles['kpi-card__trend'], 'kpi-card__trend')}>
        <span className={cn(kpiCardsStyles['kpi-card__trend-label'], 'kpi-card__trend-label')}>
          {tr('FRONTEND.DASHBOARD.KPI.NO_PREVIOUS_PERIOD', 'No prior period to compare')}
        </span>
      </div>
    )
  }

  return (
    <div className={cn(kpiCardsStyles['kpi-card__trend'], 'kpi-card__trend')}>
      <TrendPill delta={delta} />
      <span className={cn(kpiCardsStyles['kpi-card__trend-label'], 'kpi-card__trend-label')}>
        {tr('FRONTEND.DASHBOARD.KPI.VS_PREV_7_DAYS', 'vs prev. 7 days')}
      </span>
    </div>
  )
}

KpiCard.Header = Header
KpiCard.Value = Value
KpiCard.Sub = Sub
KpiCard.Trend = Trend

export default KpiCard
